import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Seo, { stripHtml } from "@/components/Seo";
import { SocialShare } from "@/components/SocialShare";
import { MediaHub } from "@/components/media/MediaHub";
import { MediaGate } from "@/components/media/MediaGate";
import { toEmbedUrl } from "@/lib/video";
import api from "@/lib/api";


import RichContent from "@/components/RichContent";
export default function Media() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [gate, setGate] = useState(null);
  // Deep link /media/:id → open that episode.
  useEffect(() => {
    setGate(null);
    if (!id) { setActive(null); return; }
    api.get(`/media/${id}`).then((r) => {
      const m = r.data;
      setActive(m);
      if (m?.slug && id === m.id) navigate(`/media/${m.slug}`, { replace: true });
    }).catch((e) => setGate(e.response?.status === 403 ? (e.response.data?.detail || { code: "members_only" }) : { code: "missing" }));
  }, [id, navigate]);

  return (
    <div data-testid="media-page">
      <Seo title={active ? active.title : "Media — Teachings, Podcasts & Vlogs"}
        description={active ? stripHtml(active.description || "") || `${active.title} — from the Enhancing Your World media library.` : "Podcasts, vlogs, interviews and recorded teachings from the Enhancing Your World wellness community."}
        image={active?.featured_image || ""} path={active ? `/media/${active.slug || active.id}` : "/media"}
        type={active ? "article" : "website"} noindex={!!gate || (active && (active.access_level || "public") !== "public")}
        jsonLd={active ? {
          "@context": "https://schema.org", "@type": active.audio_url || active.media_type === "podcast" ? "PodcastEpisode" : "VideoObject",
          name: active.title, description: stripHtml(active.description || "").slice(0, 300) || active.title,
          url: `${window.location.origin}/media/${active.slug || active.id}`, datePublished: active.publish_date || active.created_at,
          ...(active.featured_image ? { image: active.featured_image, thumbnailUrl: active.featured_image } : {}),
          ...(active.audio_url ? { associatedMedia: { "@type": "MediaObject", contentUrl: active.audio_url } } : {}),
          ...(!active.audio_url && (active.video_embed_url || active.media_url) ? { uploadDate: active.publish_date || active.created_at, embedUrl: active.video_embed_url || undefined, contentUrl: active.media_url || undefined } : {}),
          ...(active.host || active.author_name ? { creator: { "@type": "Person", name: active.host || active.author_name } } : {}),
        } : null} />
      <PageHero label="Media & Live" title="Live streams, videos & articles."
        subtitle="Everything in one place — watch live, catch a video, or read an article from the Enhancing Your World community."
        image="https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1600&q=80" testid="media-hero" />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <MediaHub />

      </section>

      {active && <MediaPlayer media={active} onClose={() => { setActive(null); if (id) navigate("/media"); }} />}
      {gate && <MediaGate gate={gate} onClose={() => navigate("/media")} />}
    </div>
  );
}

function MediaPlayer({ media, onClose }) {
  const embed = media.video_embed_url ? toEmbedUrl(media.video_embed_url) : "";
  const uploaded = media.media_url || "";
  const uploadedIsVideo = /\.(mp4|webm|mov|m4v|ogv)$/i.test(uploaded);
  return (
    <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/60 backdrop-blur-sm overflow-y-auto py-8" onClick={onClose} data-testid="media-player">
      <div className="relative w-full max-w-3xl bg-[#F9F7F3] rounded-3xl shadow-2xl overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 z-10 bg-white/90 rounded-full h-9 w-9 grid place-items-center text-[#144F75]">✕</button>
        {embed ? (
          <div className="aspect-video bg-black"><iframe src={embed} title={media.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
        ) : uploaded && uploadedIsVideo ? (
          <video src={uploaded} controls className="w-full max-h-[70vh] bg-black" data-testid="media-uploaded-video" />
        ) : uploaded ? (
          <img src={uploaded} alt={media.title} className="w-full max-h-[70vh] object-contain bg-black" />
        ) : media.featured_image ? (
          <img src={media.featured_image} alt={media.title} className="w-full max-h-72 object-cover" />
        ) : null}
        <div className="p-7">
          {media.episode_number != null && <span className="text-xs text-[#8FA89B]">Episode {media.episode_number}</span>}
          <h2 className="font-serif text-2xl text-[#1A242B]">{media.title}</h2>
          {media.host && <p className="text-sm text-[#8FA89B] mt-1">with {media.host}</p>}
          <div className="mt-2"><SocialShare path={`/media/${media.slug || media.id}`} ogPath={`/api/share/podcast/${media.id}`} title={media.title} testid="media-share" label="Share" /></div>
          {media.audio_url && (
            <audio controls src={media.audio_url} data-testid="media-audio-player" className="w-full mt-4">Your browser does not support audio.</audio>
          )}
          <RichContent text={media.description} className="text-[#4A5D6B] mt-4 leading-relaxed" testid="media-description" />
        </div>
      </div>
    </div>
  );
}
